import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../context/UserContext';
import { usePlumes } from './Plumes';
import { IoWallet, IoCash, IoCheckmarkCircle, IoLink, IoTime, IoArrowForward } from 'react-icons/io5';
import { motion, AnimatePresence } from 'framer-motion';
import './AuthorWallet.css';

const PLUME_RATE = 0.99 / 50;
const COMMISSION = 0.15;
const MIN_PAYOUT_PLUMES = 1000;
const STRIPE_ENABLED = false; // Passe à true quand Stripe Connect sera déployé

export default function AuthorWallet() {
  const { user } = useUser();
  const { plumes } = usePlumes();
  const [connectStatus, setConnectStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payoutResult, setPayoutResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const grossEuros = plumes * PLUME_RATE;
  const commission = grossEuros * COMMISSION;
  const netEuros = grossEuros - commission;
  const canPayout = plumes >= MIN_PAYOUT_PLUMES;

  useEffect(() => {
    if (!STRIPE_ENABLED) return;
    const checkStatus = async () => {
      try {
        const { getConnectStatus } = await import('../utils/stripe');
        const status = await getConnectStatus();
        setConnectStatus(status);
      } catch (e) {
        console.warn('Stripe Connect non disponible');
      }
    };
    checkStatus();
  }, []);

  const handleConnect = async () => {
    if (!STRIPE_ENABLED) return;
    setConnecting(true);
    try {
      const { createConnectAccount } = await import('../utils/stripe');
      const result = await createConnectAccount();
      window.location.href = result.url;
    } catch (e) {
      alert('La configuration du compte bancaire n\'est pas encore disponible.');
    }
    setConnecting(false);
  };

  const handlePayout = async () => {
    if (!STRIPE_ENABLED) return;
    if (!window.confirm(`Convertir tes ${plumes.toLocaleString('fr-FR')} plumes en ${netEuros.toFixed(2)}€ ?\n\n• Montant brut : ${grossEuros.toFixed(2)}€\n• Commission Plumly (15%) : -${commission.toFixed(2)}€\n• Tu reçois : ${netEuros.toFixed(2)}€`)) return;
    setPaying(true);
    try {
      const { requestPayout } = await import('../utils/stripe');
      const result = await requestPayout();
      setPayoutResult(result);
    } catch (e) {
      alert('Erreur : ' + e.message);
    }
    setPaying(false);
  };

  const loadHistory = async () => {
    if (!user) return;
    try {
      const q = query(
        collection(db, 'payouts'),
        where('userId', '==', user.id),
        orderBy('createdAt', 'desc')
      );
      const snap = await getDocs(q);
      setHistory(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      // Collection pas encore créée, pas grave
    }
  };

  return (
    <div className="wallet-card">
      <div className="wallet-header">
        <IoWallet className="wallet-icon" />
        <h3>Portefeuille auteur</h3>
      </div>

      {/* Solde */}
      <div className="wallet-balance">
        <div className="wallet-balance-plumes">
          <span className="wallet-balance-icon">🪶</span>
          <div>
            <span className="wallet-balance-value">{plumes.toLocaleString('fr-FR')}</span>
            <span className="wallet-balance-label">plumes reçues</span>
          </div>
        </div>
        <div className="wallet-balance-arrow"><IoArrowForward /></div>
        <div className="wallet-balance-euros">
          <span className="wallet-balance-value wallet-balance-green">{netEuros.toFixed(2)}€</span>
          <span className="wallet-balance-label">net après 15%</span>
        </div>
      </div>

      <div className="wallet-conversion-info">
        <span>50 🪶 = 0,99€</span>
        <span>·</span>
        <span>Commission Plumly : 15%</span>
        <span>·</span>
        <span>Min. {MIN_PAYOUT_PLUMES} 🪶</span>
      </div>

      {/* Succès versement */}
      <AnimatePresence>
        {payoutResult && (
          <motion.div
            className="wallet-payout-success"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
          >
            <IoCheckmarkCircle className="wallet-success-icon" />
            <div>
              <p className="wallet-success-title">Versement effectué !</p>
              <p className="wallet-success-detail">
                {payoutResult.plumes} plumes → {payoutResult.netEuros}€ envoyés sur ton compte
              </p>
            </div>
            <button className="wallet-success-close" onClick={() => { setPayoutResult(null); window.location.reload(); }}>OK</button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stripe pas encore activé */}
      {!STRIPE_ENABLED && (
        <div className="wallet-connect-setup">
          <div className="wallet-connect-info">
            <IoLink className="wallet-connect-icon" />
            <div>
              <p className="wallet-connect-title">Versements bientôt disponibles</p>
              <p className="wallet-connect-desc">Le système de versement est en cours de mise en place. Tu pourras bientôt convertir tes plumes en euros !</p>
            </div>
          </div>
        </div>
      )}

      {/* Stripe activé mais pas connecté */}
      {STRIPE_ENABLED && !connectStatus?.connected && (
        <div className="wallet-connect-setup">
          <div className="wallet-connect-info">
            <IoLink className="wallet-connect-icon" />
            <div>
              <p className="wallet-connect-title">Configure ton compte bancaire</p>
              <p className="wallet-connect-desc">Pour recevoir tes gains, connecte ton compte via Stripe.</p>
            </div>
          </div>
          <motion.button
            className="wallet-connect-btn"
            onClick={handleConnect}
            disabled={connecting}
            whileTap={{ scale: 0.97 }}
          >
            {connecting ? 'Redirection...' : 'Configurer mon compte bancaire'}
          </motion.button>
        </div>
      )}

      {/* Vérification en cours */}
      {STRIPE_ENABLED && connectStatus?.connected && !connectStatus?.payoutsEnabled && (
        <div className="wallet-connect-pending">
          <IoTime className="wallet-pending-icon" />
          <div>
            <p className="wallet-pending-title">Vérification en cours</p>
            <p className="wallet-pending-desc">Ton compte est en cours de vérification par Stripe (24-48h).</p>
          </div>
        </div>
      )}

      {/* Bouton versement */}
      {STRIPE_ENABLED && connectStatus?.connected && connectStatus?.payoutsEnabled && !payoutResult && (
        <motion.button
          className={'wallet-payout-btn ' + (!canPayout ? 'wallet-payout-btn-disabled' : '')}
          onClick={handlePayout}
          disabled={!canPayout || paying}
          whileTap={canPayout ? { scale: 0.97 } : {}}
        >
          <IoCash />
          {paying
            ? 'Versement en cours...'
            : canPayout
              ? `Convertir ${plumes.toLocaleString('fr-FR')} plumes → ${netEuros.toFixed(2)}€`
              : `Minimum ${MIN_PAYOUT_PLUMES} plumes requis (${plumes} actuellement)`
          }
        </motion.button>
      )}

      {/* Historique */}
      <button className="wallet-history-btn" onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}>
        {showHistory ? '▲ Masquer l\'historique' : '▼ Historique des versements'}
      </button>

      <AnimatePresence>
        {showHistory && (
          <motion.div className="wallet-history" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            {history.length > 0 ? history.map(h => (
              <div key={h.id} className="wallet-history-item">
                <div className="wallet-history-left">
                  <span className="wallet-history-amount">+{h.netEuros?.toFixed(2)}€</span>
                  <span className="wallet-history-detail">{h.plumes} plumes · -{(h.commission || 0).toFixed(2)}€ commission</span>
                </div>
                <span className="wallet-history-date">
                  {h.createdAt?.toDate ? h.createdAt.toDate().toLocaleDateString('fr-FR') : ''}
                </span>
              </div>
            )) : (
              <p className="wallet-history-empty">Aucun versement pour le moment</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}